// store/modules/auth.js
import axios from 'axios';
import VueCookies from 'vue-cookies';

import router from '../../router';

const state = {
    user: null,
    token: null,
    requires2FA: false,
    userIdFor2FA: null,
    apiUrl: process.env.VUE_APP_API_URL
};

const getters = {
    isLoggedIn: state => !!state.token,
    getUser: state => state.user,
    getToken: state => state.token,
    requires2FA: state => state.requires2FA
};

const actions = {
    async login({ commit, state }, { username, password }) {
        try {
            const formData = new FormData();
            formData.append('login', username);
            formData.append('password', password);

            const res = await axios.post(`${state.apiUrl}/login`, formData);

            if (res.data.status) {
                if (res.data.requires_2fa) {
                    commit('set2FA', { requires2FA: true, userIdFor2FA: res.data.user_id });
                    return { requires_2fa: true, user_id: res.data.user_id };
                } else {
                    commit('setAuth', { user: res.data.data, token: res.data.token });
                    VueCookies.set('sgcchris', { user: res.data.data, token: res.data.token });
                    redirectUser(res.data.data);
                    return res.data.data;
                }
            } else {
                throw new Error('Invalid credentials');
            }
        } catch (err) {
            throw err;
        }
    },

    async verifyOTP({ commit, state }, { userId, code }) {
        try {
            const formData = new FormData();
            formData.append('user_id', userId);
            formData.append('code', code);

            const res = await axios.post(`${state.apiUrl}/verify-2fa`, formData);

            if (res.data.status) {
                commit('setAuth', { user: res.data.data, token: res.data.token });
                VueCookies.set('sgcchris', { user: res.data.data, token: res.data.token });
                commit('set2FA', { requires2FA: false, userIdFor2FA: null });
                redirectUser(res.data.data);
                return res.data.data;
            } else {
                throw new Error('Invalid OTP');
            }
        } catch (err) {
            throw err;
        }
    },

    async loginViaEmployeeId({ commit, state }, { employeeId }) {
        try {
            const formData = new FormData();
            formData.append('employee_id', employeeId);

            const res = await axios.post(`${state.apiUrl}/login/id`, formData);

            if (res.data.status) {
                commit('setAuth', { user: res.data.data, token: res.data.token });
                VueCookies.set('sgcchris', { user: res.data.data, token: res.data.token });
                redirectUser(res.data.data);
                return res.data.data;
            } else {
                throw new Error('Invalid Employee ID');
            }
        } catch (err) {
            throw err;
        }
    },

    loadFromCookie({ commit }) {
        const cookieData = VueCookies.get('sgcchris');
        if (cookieData && cookieData.token) {
            commit('setAuth', { user: cookieData.user, token: cookieData.token });
        }
    },

    async logout({ commit, state }) {
        try {
            await axios.post(`${state.apiUrl}/logout`, null, {
                headers: {
                    Authorization: `Bearer ${state.token}`
                }
            });
        } catch (err) {
            console.warn('Audit log: logout request failed', err.message || err);
        }
        VueCookies.remove('sgcchris');
        commit('clearAuth');
        location.reload();
    },

    updateImage({ commit }, imageUrl) {
        commit('updateUserImage', imageUrl);
    }
};

const mutations = {
    setAuth(state, { user, token }) {
        state.user = user;
        state.token = token;
    },
    updateUser(state, user) {
        state.user = user;
        const cookieData = VueCookies.get('sgcchris') || {};
        cookieData.user = user;
        VueCookies.set('sgcchris', cookieData);
    },
    updateUserImage(state, newImageUrl) {
        if (state.user) {
            state.user.image = newImageUrl;
            const cookieData = VueCookies.get('sgcchris') || {};
            if (cookieData.user) {
                cookieData.user.image = newImageUrl;
                VueCookies.set('sgcchris', cookieData);
            }
        }
    },
    clearAuth(state) {
        state.user = null;
        state.token = null;
        state.requires2FA = false;
        state.userIdFor2FA = null;
    },
    set2FA(state, { requires2FA, userIdFor2FA }) {
        state.requires2FA = requires2FA;
        state.userIdFor2FA = userIdFor2FA;
    }
};

// Redirect helper
function redirectUser(userData) {
    setTimeout(() => {
        const isSaasOwner = Boolean(userData?.is_saas_owner ?? userData?.is_sass_owner);
        const isSuperAdmin = Number(userData.role_id) === 0;
        if (isSuperAdmin || isSaasOwner) {
            router.push('/settings/saas/dashboard');
        } else if (userData.role_id === 1) {
            router.push('/employee-management/dashboard');
        } else {
            router.push('/employee/attendance');
        }
    }, 500);
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
